import {
  BeakerIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  FunnelIcon,
  GlobeAltIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MegaphoneIcon,
  PlusIcon,
  ServerIcon,
  Squares2X2Icon,
  TagIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  AnalyticsFilterField,
  AnalyticsFilterFieldsMeta,
  AnalyticsFilterOperator,
  AnalyticsFilterRule,
  AnalyticsFilters,
  MultiValueOperators,
  NoValueOperators,
  OperatorLabels,
  OperatorsByFieldType,
  TextOperators,
} from '@superfunnel/types';
import Button, { ButtonProps } from '@superfunnel/ui-core/v2/Button';
import ChipsAutocomplete from '@superfunnel/ui-core/v2/ChipsAutocomplete';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Bot } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  FilterOption,
  useAnalyticsFilterOptions,
} from '../../hooks/useAnalyticsFilterOptions';
import { classNames } from '../../utils';

/**
 * Icons for each filter field
 */
const FieldIcons: Record<
  AnalyticsFilterField,
  React.ComponentType<{ className?: string }>
> = {
  campaignId: MegaphoneIcon,
  variantId: Squares2X2Icon,
  websiteId: BeakerIcon,
  device: DevicePhoneMobileIcon,
  os: ComputerDesktopIcon,
  browser: GlobeAltIcon,
  country: MapPinIcon,
  city: MapPinIcon,
  adNetworkProvider: MegaphoneIcon,
  referrerDomain: LinkIcon,
  origin: LinkIcon,
  search: MagnifyingGlassIcon,
  utmSource: TagIcon,
  utmMedium: TagIcon,
  isBot: Bot,
  botName: Bot,
  environment: ServerIcon,
};

/**
 * Generate a unique ID for a filter rule
 */
function generateRuleId(): string {
  return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get the field metadata for a given field
 */
function getFieldMeta(field: AnalyticsFilterField) {
  return AnalyticsFilterFieldsMeta.find((f) => f.field === field);
}

/**
 * Get the default operator for a field type
 */
function getDefaultOperator(
  fieldType: 'string' | 'select' | 'boolean' | 'enum',
  fieldMeta?: ReturnType<typeof getFieldMeta>,
): AnalyticsFilterOperator {
  if (fieldType === 'boolean') return 'is';
  if (fieldType === 'enum' || fieldType === 'select') return 'isAnyOf';
  // String fields without options default to text-based 'contains'
  if (!fieldMeta?.dynamicOptions && !fieldMeta?.staticOptions)
    return 'contains';
  return 'isAnyOf';
}

interface FilterRuleRowProps {
  rule: AnalyticsFilterRule;
  options: Record<AnalyticsFilterField, FilterOption[]>;
  availableFields: typeof AnalyticsFilterFieldsMeta;
  onUpdate: (rule: AnalyticsFilterRule) => void;
  onRemove: () => void;
  isFirst: boolean;
}

function FilterRuleRow({
  rule,
  options,
  availableFields,
  onUpdate,
  onRemove,
  isFirst,
}: FilterRuleRowProps) {
  const fieldMeta = getFieldMeta(rule.field);
  const fieldType = fieldMeta?.type || 'string';
  const hasSelectableOptions =
    fieldMeta?.dynamicOptions || !!fieldMeta?.staticOptions;

  // Dynamically compute available operators based on field type and options
  const availableOperators: AnalyticsFilterOperator[] = useMemo(() => {
    const baseOperators = OperatorsByFieldType[fieldType];
    // For string fields without selectable options (e.g. search),
    // replace isAnyOf/isNotAnyOf with is/isNot (rendered as text inputs)
    if (fieldType === 'string' && !hasSelectableOptions) {
      return [
        ...baseOperators.filter(
          (op) => op !== 'isAnyOf' && op !== 'isNotAnyOf',
        ),
        'is' as AnalyticsFilterOperator,
        'isNot' as AnalyticsFilterOperator,
      ];
    }
    return baseOperators;
  }, [fieldType, hasSelectableOptions]);

  const needsValue = !NoValueOperators.includes(rule.operator);
  const isMultiValue = MultiValueOperators.includes(rule.operator);
  const isTextOperator = TextOperators.includes(rule.operator);

  // Debounced local state for text input
  const [localTextValue, setLocalTextValue] = useState(
    isTextOperator ? String(rule.value || '') : '',
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local text value when rule.value changes externally (e.g. field/operator change)
  useEffect(() => {
    if (isTextOperator) {
      setLocalTextValue(String(rule.value || ''));
    }
  }, [rule.field, rule.operator]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTextValueChange = useCallback(
    (newValue: string) => {
      setLocalTextValue(newValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdate({ ...rule, value: newValue });
      }, 500);
    },
    [rule, onUpdate],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Get options for the current field
  const fieldOptions = useMemo(
    () => options[rule.field] || [],
    [options, rule.field],
  );

  // Handle field change
  const handleFieldChange = (newField: string) => {
    const newFieldMeta = getFieldMeta(newField as AnalyticsFilterField);
    const newFieldType = newFieldMeta?.type || 'string';
    const newOperator = getDefaultOperator(newFieldType, newFieldMeta);
    const isNewMultiValue = MultiValueOperators.includes(newOperator);

    onUpdate({
      ...rule,
      field: newField as AnalyticsFilterField,
      operator: newOperator,
      value: isNewMultiValue ? [] : null,
    });
  };

  // Handle operator change
  const handleOperatorChange = (newOperator: string) => {
    const isNewMultiValue = MultiValueOperators.includes(
      newOperator as AnalyticsFilterOperator,
    );
    const isNewNoValue = NoValueOperators.includes(
      newOperator as AnalyticsFilterOperator,
    );

    let newValue: AnalyticsFilterRule['value'] = rule.value;

    if (isNewNoValue) {
      newValue = null;
    } else if (isNewMultiValue && !Array.isArray(rule.value)) {
      newValue = rule.value ? [String(rule.value)] : [];
    } else if (!isNewMultiValue && Array.isArray(rule.value)) {
      newValue = rule.value[0] || null;
    }

    onUpdate({
      ...rule,
      operator: newOperator as AnalyticsFilterOperator,
      value: newValue,
    });
  };

  // Handle value change for multi-select
  const handleMultiValueChange = (selected: FilterOption[]) => {
    onUpdate({
      ...rule,
      value: selected.map((s) => s.value),
    });
  };

  // Handle value change for single select (boolean)
  const handleSingleValueChange = (newValue: string) => {
    onUpdate({
      ...rule,
      value: newValue,
    });
  };

  // Get selected options for multi-value
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(rule.value)) return [];
    const values = rule.value as string[];
    return fieldOptions.filter((opt) => values.includes(opt.value));
  }, [rule.value, fieldOptions]);

  return (
    <div className="flex items-center gap-2">
      {/* Where/And label */}
      <div className="rounded-2lg flex h-10 w-[73px] shrink-0 items-center justify-center bg-gray-50 px-3">
        <span className="text-base text-[#637083]">
          {isFirst ? 'Where' : 'And'}
        </span>
      </div>

      {/* Field selector */}
      <Select value={rule.field} onValueChange={handleFieldChange}>
        <SelectTrigger className="rounded-2lg h-10 max-w-36 flex-1">
          <SelectValue placeholder="Select field">
            {(() => {
              const Icon = FieldIcons[rule.field];
              const meta = availableFields.find((f) => f.field === rule.field);
              return (
                <>
                  <Icon className="text-base-400 h-4 w-4" />
                  {meta?.label}
                </>
              );
            })()}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {availableFields.map((field) => {
            const Icon = FieldIcons[field.field];
            return (
              <SelectItem key={field.field} value={field.field}>
                <Icon className="text-base-400 h-4 w-4" />
                {field.label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {/* Operator selector */}
      <Select value={rule.operator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="rounded-2lg h-10 w-[120px] shrink-0">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {availableOperators.map((op) => (
            <SelectItem key={op} value={op}>
              {OperatorLabels[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input */}
      <div className="min-w-[120px] flex-1">
        {needsValue && (
          <>
            {fieldType === 'boolean' ? (
              <Select
                value={String(rule.value)}
                onValueChange={handleSingleValueChange}
              >
                <SelectTrigger className="rounded-2lg h-10 w-full">
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            ) : isTextOperator ? (
              <input
                type="text"
                value={localTextValue}
                onChange={(e) => handleTextValueChange(e.target.value)}
                placeholder="Enter value..."
                className="rounded-2lg border-base-200 placeholder:text-base-400 focus:border-brand-500 focus:ring-brand-500 h-10 w-full border bg-white px-3 text-sm shadow-sm transition-colors focus:ring-1 focus:outline-none"
              />
            ) : isMultiValue ? (
              <ChipsAutocomplete<FilterOption>
                placeholder="Select values..."
                options={fieldOptions}
                getOptionId={(opt) => opt.value}
                getValue={(opt) => opt.label}
                selected={selectedOptions}
                onChange={handleMultiValueChange}
                className="w-full"
              />
            ) : (
              <Select
                value={String(rule.value || '')}
                onValueChange={handleSingleValueChange}
              >
                <SelectTrigger className="rounded-2lg h-10 w-full">
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent>
                  {fieldOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </>
        )}
      </div>

      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-2lg flex h-10 w-10 shrink-0 items-center justify-center border border-[#ffc2c2] bg-white shadow-sm transition-colors hover:bg-red-50"
      >
        <TrashIcon className="h-5 w-5 text-[#F62C2C]" />
      </button>
    </div>
  );
}

interface AnalyticsFilterBuilderProps {
  size?: ButtonProps['size'];
  className?: string;
  /**
   * Mode determines which fields are available:
   * - 'account': Shows all fields including campaignId (for account-wide analytics)
   * - 'dynamicPage': Hides campaignId, shows variantId and websiteId (for single dynamic page analytics)
   */
  mode?: 'account' | 'dynamicPage';
  /**
   * Variant options for the variantId field (only used when mode is 'dynamicPage')
   */
  variantOptions?: FilterOption[];
  /**
   * Experiment variation options for the websiteId field (only used when mode is 'dynamicPage').
   * If empty or not provided, the websiteId field will be hidden.
   */
  experimentOptions?: FilterOption[];
  /**
   * The current filter state
   */
  filters: AnalyticsFilters;
  /**
   * Callback to update the filter state
   */
  onFiltersChange: (filters: AnalyticsFilters) => void;
  /**
   * Callback to reset filters to empty state
   */
  onFiltersReset: () => void;
}

export default function AnalyticsFilterBuilder({
  size = 'sm',
  className,
  mode = 'account',
  variantOptions = [],
  experimentOptions = [],
  filters,
  onFiltersChange,
  onFiltersReset,
}: AnalyticsFilterBuilderProps) {
  const { options, isLoading } = useAnalyticsFilterOptions();

  const activeFiltersCount = filters.rules.length;

  // Filter available fields based on mode
  const availableFields = useMemo(() => {
    if (mode === 'dynamicPage') {
      // Hide campaignId, show variantId
      // Only show websiteId if there are experiment options
      return AnalyticsFilterFieldsMeta.filter((f) => {
        if (f.field === 'campaignId') return false;
        if (f.field === 'websiteId' && experimentOptions.length === 0)
          return false;
        return true;
      });
    }
    // Account mode: hide variantId and websiteId, show campaignId
    return AnalyticsFilterFieldsMeta.filter(
      (f) => f.field !== 'variantId' && f.field !== 'websiteId',
    );
  }, [mode, experimentOptions.length]);

  // Add a new filter rule
  const handleAddRule = useCallback(() => {
    const newRule: AnalyticsFilterRule = {
      id: generateRuleId(),
      field: 'device',
      operator: 'isAnyOf',
      value: [],
    };

    onFiltersChange({
      ...filters,
      rules: [...filters.rules, newRule],
    });
  }, [filters, onFiltersChange]);

  // Update a filter rule
  const handleUpdateRule = useCallback(
    (updatedRule: AnalyticsFilterRule) => {
      onFiltersChange({
        ...filters,
        rules: filters.rules.map((rule) =>
          rule.id === updatedRule.id ? updatedRule : rule,
        ),
      });
    },
    [filters, onFiltersChange],
  );

  // Remove a filter rule
  const handleRemoveRule = useCallback(
    (ruleId: string) => {
      onFiltersChange({
        ...filters,
        rules: filters.rules.filter((rule) => rule.id !== ruleId),
      });
    },
    [filters, onFiltersChange],
  );

  // Build the options record for the FilterRuleRow
  const optionsRecord = useMemo(
    () =>
      ({
        campaignId: options.campaignId,
        variantId: variantOptions,
        websiteId: experimentOptions,
        device: options.device,
        os: options.os,
        browser: options.browser,
        country: options.country,
        city: options.city,
        adNetworkProvider: options.adNetworkProvider,
        referrerDomain: options.referrerDomain,
        origin: options.origin,
        search: [],
        utmSource: options.utmSource,
        utmMedium: options.utmMedium,
        isBot: options.isBot,
        botName: options.botName,
        environment: options.environment,
      }) as Record<AnalyticsFilterField, FilterOption[]>,
    [options, variantOptions, experimentOptions],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className={classNames('gap-2', className)}
        >
          <span className="relative">
            <FunnelIcon className="h-4 w-4" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-[13px] w-[13px] rounded border border-white bg-[#ff4d4d] text-center text-[9px] font-medium text-white">
                {activeFiltersCount}
              </span>
            )}
          </span>
          Filter
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[650px] rounded-[15px] border border-[#e4e7ec] bg-white p-3 shadow-[0px_20px_25px_-5px_rgba(20,28,37,0.1)]"
      >
        <div className="flex flex-col gap-3">
          {/* Filter rows */}
          <div className="flex flex-col gap-3">
            {isLoading && filters.rules.length === 0 && (
              <div className="py-4 text-center text-base text-[#637083]">
                Loading filter options...
              </div>
            )}

            {filters.rules.length === 0 && !isLoading && (
              <div className="py-4 text-center text-base text-[#637083]">
                No filters applied. Click &quot;Add filter&quot; to get started.
              </div>
            )}

            {filters.rules.map((rule, index) => (
              <FilterRuleRow
                key={rule.id}
                rule={rule}
                options={optionsRecord}
                availableFields={availableFields}
                onUpdate={handleUpdateRule}
                onRemove={() => handleRemoveRule(rule.id)}
                isFirst={index === 0}
              />
            ))}
          </div>

          {/* Divider */}
          <div className="h-px w-full bg-[#E4E7EC]" />

          {/* Footer */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddRule}
              className="gap-1 text-[#795dfc] hover:text-[#6344e0]"
            >
              <PlusIcon className="h-5 w-5" />
              Add filter
            </Button>

            {activeFiltersCount > 0 && (
              <Button variant="outline" size="sm" onClick={onFiltersReset}>
                Clear all filters
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
